import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Search,
  Filter,
  Download,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Eye,
  Calendar,
  User,
  Activity,
  Shield,
  Clock,
  TrendingUp,
  AlertCircle,
  X,
  ChevronDown,
} from 'lucide-react';
import { apiGet } from '@/utils/api';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { UserRole } from '@/types/global.types';

// Types
interface AuditLog {
  id: string;
  tenantId?: string;
  userId?: string;
  userEmail?: string;
  userRole?: string;
  action: string;
  category: string;
  resource: string;
  resourceId?: string;
  oldValue?: Record<string, unknown>;
  newValue?: Record<string, unknown>;
  description?: string;
  ipAddress?: string;
  userAgent?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

interface AuditStatistics {
  period: { days: number; startDate: string; endDate: string };
  totalLogs: number;
  byCategory: { category: string; count: number }[];
  byAction: { action: string; count: number }[];
  loginCount: number;
}

interface PaginatedResponse {
  data: AuditLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Action and Category labels in French
const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  CREATE: { label: 'Création', color: 'bg-green-100 text-green-800' },
  UPDATE: { label: 'Modification', color: 'bg-blue-100 text-blue-800' },
  DELETE: { label: 'Suppression', color: 'bg-red-100 text-red-800' },
  VIEW: { label: 'Consultation', color: 'bg-gray-100 text-gray-800' },
  EXPORT: { label: 'Export', color: 'bg-purple-100 text-purple-800' },
  LOGIN: { label: 'Connexion', color: 'bg-green-100 text-green-800' },
  LOGOUT: { label: 'Déconnexion', color: 'bg-gray-100 text-gray-800' },
  LOGIN_FAILED: { label: 'Échec connexion', color: 'bg-red-100 text-red-800' },
  PASSWORD_CHANGE: { label: 'Changement MDP', color: 'bg-yellow-100 text-yellow-800' },
  PERMISSION_CHANGE: { label: 'Permissions', color: 'bg-orange-100 text-orange-800' },
  CONSENT_GIVEN: { label: 'Consentement', color: 'bg-green-100 text-green-800' },
  CONSENT_WITHDRAWN: { label: 'Retrait consent.', color: 'bg-red-100 text-red-800' },
  DATA_EXPORT_REQUEST: { label: 'Export données', color: 'bg-purple-100 text-purple-800' },
  DELETION_REQUEST: { label: 'Demande suppr.', color: 'bg-red-100 text-red-800' },
};

const CATEGORY_LABELS: Record<string, { label: string; icon: React.ReactNode }> = {
  AUTH: { label: 'Authentification', icon: <Shield size={14} /> },
  USER: { label: 'Utilisateurs', icon: <User size={14} /> },
  STUDENT: { label: 'Étudiants', icon: <User size={14} /> },
  STAFF: { label: 'Personnel', icon: <User size={14} /> },
  PAYMENT: { label: 'Paiements', icon: <FileText size={14} /> },
  GRADE: { label: 'Notes', icon: <FileText size={14} /> },
  PERMISSION: { label: 'Permissions', icon: <Shield size={14} /> },
  DOCUMENT: { label: 'Documents', icon: <FileText size={14} /> },
  COMPLIANCE: { label: 'CNDP', icon: <Shield size={14} /> },
  SYSTEM: { label: 'Système', icon: <Activity size={14} /> },
};

// Detail Modal Component
const DetailModal: React.FC<{
  log: AuditLog;
  onClose: () => void;
}> = ({ log, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-blue-600 to-indigo-600">
          <h3 className="text-lg font-semibold text-white">Détails de l'action</h3>
          <button
            onClick={onClose}
            className="p-1 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <p className="text-sm text-gray-500">Action</p>
              <span
                className={`inline-flex px-2 py-1 rounded-full text-xs font-medium mt-1 ${
                  ACTION_LABELS[log.action]?.color || 'bg-gray-100 text-gray-800'
                }`}
              >
                {ACTION_LABELS[log.action]?.label || log.action}
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-500">Catégorie</p>
              <p className="font-medium text-gray-900 mt-1">
                {CATEGORY_LABELS[log.category]?.label || log.category}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Utilisateur</p>
              <p className="font-medium text-gray-900 mt-1">
                {log.userEmail || 'Non identifié'}
              </p>
              {log.userRole && (
                <p className="text-xs text-gray-500">{log.userRole}</p>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500">Date et heure</p>
              <p className="font-medium text-gray-900 mt-1">
                {new Date(log.createdAt).toLocaleString('fr-FR')}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Ressource</p>
              <p className="font-medium text-gray-900 mt-1">{log.resource}</p>
              {log.resourceId && (
                <p className="text-xs text-gray-500 font-mono">{log.resourceId}</p>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500">Adresse IP</p>
              <p className="font-medium text-gray-900 mt-1 font-mono text-sm">
                {log.ipAddress || 'N/A'}
              </p>
            </div>
          </div>

          {log.description && (
            <div className="mb-4">
              <p className="text-sm text-gray-500 mb-1">Description</p>
              <p className="text-gray-900">{log.description}</p>
            </div>
          )}

          {(log.oldValue || log.newValue) && (
            <div className="space-y-4">
              {log.oldValue && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Ancienne valeur</p>
                  <pre className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm overflow-x-auto">
                    {JSON.stringify(log.oldValue, null, 2)}
                  </pre>
                </div>
              )}
              {log.newValue && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Nouvelle valeur</p>
                  <pre className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm overflow-x-auto">
                    {JSON.stringify(log.newValue, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const AuditLogsPage: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [statistics, setStatistics] = useState<AuditStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [actionFilter, setActionFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState<{ start?: string; end?: string }>({});
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  // Available filters
  const [categories, setCategories] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (categoryFilter) params.set('category', categoryFilter);
      if (actionFilter) params.set('action', actionFilter);
      if (dateRange.start) params.set('startDate', dateRange.start);
      if (dateRange.end) params.set('endDate', dateRange.end);

      const [logsRes, statsRes, categoriesRes, actionsRes] = await Promise.all([
        apiGet<PaginatedResponse>(`/audit-logs?${params.toString()}`),
        apiGet<AuditStatistics>('/audit-logs/statistics'),
        apiGet<string[]>('/audit-logs/categories'),
        apiGet<string[]>('/audit-logs/actions'),
      ]);

      setLogs(logsRes.data);
      setTotalPages(logsRes.pagination.totalPages);
      setTotal(logsRes.pagination.total);
      setStatistics(statsRes);
      setCategories(categoriesRes);
      setActions(actionsRes);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
    } finally {
      setLoading(false);
    }
  }, [page, categoryFilter, actionFilter, dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (categoryFilter) params.set('category', categoryFilter);
      if (actionFilter) params.set('action', actionFilter);
      if (dateRange.start) params.set('startDate', dateRange.start);
      if (dateRange.end) params.set('endDate', dateRange.end);

      const response = await fetch(`/api/audit-logs/export?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('nexedu_access_token')}`,
        },
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to export:', err);
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setCategoryFilter('');
    setActionFilter('');
    setDateRange({});
    setPage(1);
  };

  // Filter logs by search
  const filteredLogs = logs.filter((log) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      log.userEmail?.toLowerCase().includes(searchLower) ||
      log.description?.toLowerCase().includes(searchLower) ||
      log.resource.toLowerCase().includes(searchLower) ||
      log.action.toLowerCase().includes(searchLower)
    );
  });

  const hasActiveFilters = categoryFilter || actionFilter || dateRange.start || dateRange.end;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Journal d'Audit</h1>
              <p className="text-sm text-gray-500 mt-1">
                Suivi des actions et conformité CNDP
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={loadData}
                disabled={loading}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={handleExport}
                disabled={exporting}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Download size={18} />
                {exporting ? 'Export...' : 'Exporter CSV'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Activity size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total actions</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {statistics.totalLogs.toLocaleString('fr-FR')}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <User size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Connexions (30j)</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {statistics.loginCount.toLocaleString('fr-FR')}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp size={20} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Catégories actives</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {statistics.byCategory.length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Clock size={20} className="text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Période</p>
                  <p className="text-lg font-bold text-gray-900">
                    {statistics.period.days} jours
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border mb-6">
          <div className="p-4 border-b">
            <div className="flex items-center gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Rechercher par email, description, ressource..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Toggle Filters */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  hasActiveFilters
                    ? 'border-blue-300 bg-blue-50 text-blue-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter size={18} />
                Filtres
                {hasActiveFilters && (
                  <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {[categoryFilter, actionFilter, dateRange.start, dateRange.end].filter(Boolean).length}
                  </span>
                )}
                <ChevronDown
                  size={16}
                  className={`transition-transform ${showFilters ? 'rotate-180' : ''}`}
                />
              </button>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Effacer
                </button>
              )}
            </div>

            {/* Expanded Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Catégorie
                  </label>
                  <select
                    value={categoryFilter}
                    onChange={(e) => {
                      setCategoryFilter(e.target.value);
                      setPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Toutes</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {CATEGORY_LABELS[cat]?.label || cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Action
                  </label>
                  <select
                    value={actionFilter}
                    onChange={(e) => {
                      setActionFilter(e.target.value);
                      setPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Toutes</option>
                    {actions.map((action) => (
                      <option key={action} value={action}>
                        {ACTION_LABELS[action]?.label || action}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date début
                  </label>
                  <input
                    type="date"
                    value={dateRange.start || ''}
                    onChange={(e) => {
                      setDateRange((prev) => ({ ...prev, start: e.target.value }));
                      setPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date fin
                  </label>
                  <input
                    type="date"
                    value={dateRange.end || ''}
                    onChange={(e) => {
                      setDateRange((prev) => ({ ...prev, end: e.target.value }));
                      setPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date/Heure
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Catégorie
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ressource
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <div className="flex items-center justify-center gap-3">
                        <RefreshCw size={20} className="animate-spin text-blue-600" />
                        <span className="text-gray-500">Chargement...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <AlertCircle size={40} className="mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500">Aucun log trouvé</p>
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(log.createdAt).toLocaleDateString('fr-FR')}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(log.createdAt).toLocaleTimeString('fr-FR')}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-900">{log.userEmail || 'N/A'}</p>
                        {log.userRole && (
                          <p className="text-xs text-gray-500">{log.userRole}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            ACTION_LABELS[log.action]?.color || 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {ACTION_LABELS[log.action]?.label || log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {CATEGORY_LABELS[log.category]?.icon}
                          <span className="text-sm text-gray-700">
                            {CATEGORY_LABELS[log.category]?.label || log.category}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-900">{log.resource}</p>
                        {log.resourceId && (
                          <p className="text-xs text-gray-500 font-mono truncate max-w-[100px]">
                            {log.resourceId}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-600 truncate max-w-[200px]">
                          {log.description || '-'}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setSelectedLog(log)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Eye size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Affichage de {(page - 1) * limit + 1} à {Math.min(page * limit, total)} sur{' '}
                {total.toLocaleString('fr-FR')} résultats
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={20} />
                </button>
                <span className="px-3 py-1 text-sm text-gray-700">
                  Page {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedLog && <DetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />}
    </div>
  );
};

export default AuditLogsPage;
